import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BcmApiService } from './bcm-api.service';
import { parseHttpResponsePage, parseHttpResponseItem, parseHttpResponseList } from '@shared/functions/parse-http-response';
import { IBoat, Boat } from '@shared/models/boat';
import { BoatOption, IBoatOption } from '@shared/models/boat-option';
import { BcmDocument, BcmDocumentDto } from '@shared/models/bcm-document';
import { Page } from '@shared/models/pagination';
import {
    IWinterStorageBoatAssignment,
    WinterStorageBoatAssignment
} from '@shared/models/winter-storage-boat-assignment';
import { ElectricMeterAssignment, IElectricMeterAssignment } from '@shared/models/electric-meter-assignment';

// One of a boat's currently running berth assignments (≈ a booking) — the data
// needed to label the choice and link a connection to it.
export interface CurrentBookingAssignment {
    id: number;                 // = berthAssignmentId
    from: string | Date;
    to: string | Date | null;
    bookingId: number | null;
    booking?: { id: number | null } | null;
    berth?: { handle: string; pier?: { handle: string } | null } | null;
}

@Injectable({
    providedIn: 'root'
})
export class BoatApiService extends BcmApiService {

    private RESOURCE_ENDPOINT = 'boats';

    getAll(params: HttpParams = new HttpParams()): Observable<Page<Boat>> {
        return super.get<Page<IBoat>>(this.RESOURCE_ENDPOINT, params)
            .pipe(parseHttpResponsePage<IBoat, Boat>(Boat));
    }

    /**
     * Slim list endpoint backed by `BoatController.index`. Returns a paginated
     * `{ results, total }` payload using `BOAT_INDEX_COLUMNS` and the
     * `applyBoatIndexGraph` relation set — meant for selection UIs that only
     * need the boat plus a thin owner/berth hint and don't want to drag the
     * full `BoatPageModel` graph (image, absences, electric meter assignments,
     * etc.) over the wire. Search column is `filter`.
     */
    index(params: HttpParams = new HttpParams()): Observable<Page<Boat>> {
        return super.get<Page<IBoat>>(`${this.RESOURCE_ENDPOINT}/index`, params)
            .pipe(parseHttpResponsePage<IBoat, Boat>(Boat));
    }

    getOptions(params: HttpParams = new HttpParams()): Observable<Page<BoatOption>> {
        return super.get<Page<IBoatOption>>(`${this.RESOURCE_ENDPOINT}/options`, params)
            .pipe(parseHttpResponsePage<IBoatOption, BoatOption>(BoatOption));
    }

    /** Distinct boat types (Bootsart) of the tenant, deduplicated and sorted by the API. */
    getTypes(): Observable<string[]> {
        return super.get<string[]>(`${this.RESOURCE_ENDPOINT}/types`);
    }

    getOne(id: number, params: HttpParams = new HttpParams()): Observable<Boat> {
        return super.get<IBoat>(`${this.RESOURCE_ENDPOINT}/${id}`, params)
            .pipe(parseHttpResponseItem<IBoat, Boat>(Boat));
    }

    getWinterStorageAssignments(
        boatId: number,
        params: HttpParams = new HttpParams()
    ): Observable<WinterStorageBoatAssignment[]> {
        return super.get<IWinterStorageBoatAssignment[]>(`${this.RESOURCE_ENDPOINT}/${boatId}/winter-storage-assignments`, params)
            .pipe(parseHttpResponseList<IWinterStorageBoatAssignment, WinterStorageBoatAssignment>(WinterStorageBoatAssignment));
    }

    // Still-open electric-meter assignments (`to IS NULL`) of a boat, with the full
    // relation graph (cabinet/provider, productType, reading, product, berth). Used
    // by the booking-v2 wizard to propose them for take-over (`?open=true`).
    getOpenElectricMeterAssignments(boatId: number): Observable<ElectricMeterAssignment[]> {
        return super.get<IElectricMeterAssignment[]>(
            `${this.RESOURCE_ENDPOINT}/${boatId}/electric-meters`,
            new HttpParams().set('open', 'true'),
        ).pipe(parseHttpResponseList<IElectricMeterAssignment, ElectricMeterAssignment>(ElectricMeterAssignment));
    }

    // All of a boat's currently running berth assignments (≈ coexisting bookings).
    // Used to let the user choose which booking a new connection/reading links to.
    getCurrentBookingAssignments(boatId: number): Observable<CurrentBookingAssignment[]> {
        return super.get<{ assignments: CurrentBookingAssignment[] }>(`${this.RESOURCE_ENDPOINT}/${boatId}/assignments/current`)
            .pipe(map(response => response?.assignments ?? []));
    }

    add(boat: IBoat): Observable<Boat> {
        return super.post<IBoat>(this.RESOURCE_ENDPOINT, boat)
            .pipe(parseHttpResponseItem<IBoat, Boat>(Boat));
    }

    update(boat: Boat, ownershipDate?: Date): Observable<Boat> {
        return super.put<IBoat>(`${this.RESOURCE_ENDPOINT}/${boat.id}`, {boat, ownershipDate})
            .pipe(parseHttpResponseItem<IBoat, Boat>(Boat));
    }

    remove(boat: Boat): Observable<number> {
        return super.delete(`${this.RESOURCE_ENDPOINT}/${boat.id}`);
    }

    getContractDocuments(boatId: number, bookingIds: number[] = []): Observable<BcmDocument[]> {
        let params = new HttpParams();
        if (bookingIds.length) {
            params = params.set('bookingIds', bookingIds.join(','));
        }
        return super.get<BcmDocumentDto[]>(`${this.RESOURCE_ENDPOINT}/${boatId}/documents/contracts`, params)
            .pipe(parseHttpResponseList<BcmDocumentDto, BcmDocument>(BcmDocument));
    }

    protected getDocument(boatId: number, documentId: number): Observable<BcmDocument> {
        return super.get<BcmDocumentDto>(`${this.RESOURCE_ENDPOINT}/${boatId}/documents/${documentId}`)
            .pipe(parseHttpResponseItem<BcmDocumentDto, BcmDocument>(BcmDocument));
    }

    protected addDocument(boatId: number, document: BcmDocument): Observable<BcmDocument> {
        return super.post<BcmDocumentDto>(`${this.RESOURCE_ENDPOINT}/${boatId}/documents`, document)
            .pipe(parseHttpResponseItem<BcmDocumentDto, BcmDocument>(BcmDocument));
    }

    protected updateDocument(boatId: number, document: BcmDocument, documentData: Partial<BcmDocument>): Observable<BcmDocument> {
        return super.put<BcmDocumentDto>(`${this.RESOURCE_ENDPOINT}/${boatId}/documents/${document.id}`, documentData)
            .pipe(parseHttpResponseItem<BcmDocumentDto, BcmDocument>(BcmDocument));
    }

    protected deleteDocument(boatId: number, documentId: number): Observable<any> {
        return super.delete(`${this.RESOURCE_ENDPOINT}/${boatId}/documents/${documentId}`);
    }
    
}
